import { DateTimeUtils } from '@common/utils';
import { defaultTo, first } from 'lodash';
import { DateTime } from 'luxon';
import { EntityScheduleType, WeekDay } from '../Enums';
import { Model } from '../Model';
import { ComputedSchedulePreference, ProcessedHospitalScheduleModel } from '.';
import { HospitalScheduleRequestModel } from '../requests';
import { schedule } from '..';
import { petcoPewterBlue } from '@common/_assets/color-constants.js';

/**
 * A generic combined class for a schedule vet or template on a given day at a hospital.  entityId will be either vet id or template id, and entityType determines whether it's a vet or a template.
 * The unique id is a calculated field that you should use if you need an ID from this type, because vet's and templates could have conflicting id's, the uniqueId is the entityId_entityTypeId,
 * i.e. 3146_0 which would be for a vet, because 0 is the type id for a vet.  Use this id for calendar events etc to prevent having events with duplicate ids.  Also use it for resource columns
 * to prevent resources from conflicting and causing events to render wrong or disappear.
 */
export class HospitalScheduleDayEntityModel extends Model {
  hospitalScheduleId: number;
  hospitalId: number;
  day: DateTime;
  entityType: EntityScheduleType;
  entityId: number;
  isGloballyScheduled: boolean;
  sortOrder: number;
  isAdhoc: boolean;
  entityName: string | undefined;
  schedulingColor: string | undefined;
  computedSchedulePreferences: ComputedSchedulePreference[];
  hasSchedulePreferences: boolean;
  hasInheritedSchedulePreferences: boolean;
  defaultScheduleTemplateId: number | undefined;
  entityTitle: string;
  isActive: boolean;

  constructor(init?: Partial<HospitalScheduleDayEntityModel>) {
    super(init);
    this.hospitalScheduleId = defaultTo(init?.hospitalScheduleId, 0);
    this.hospitalId = defaultTo(init?.hospitalId, 0);
    this.day = DateTimeUtils.getAsDateTimeUTC(
      init?.day ? init?.day : DateTime.utc(),
    )!;
    this.entityType = defaultTo(
      init?.entityType,
      EntityScheduleType.Veterinarian,
    );
    this.entityId = defaultTo(init?.entityId, 0);
    this.isGloballyScheduled = defaultTo(init?.isGloballyScheduled, false);
    this.sortOrder = defaultTo(init?.sortOrder, 0);
    this.isAdhoc = defaultTo(init?.isAdhoc, false);
    this.entityName = defaultTo(init?.entityName, undefined);
    this.schedulingColor = defaultTo(init?.schedulingColor, petcoPewterBlue);
    this.computedSchedulePreferences =
      init?.computedSchedulePreferences?.map(
        (cp) => new ComputedSchedulePreference(cp),
      ) ?? ([] as ComputedSchedulePreference[]);
    this.hasSchedulePreferences = defaultTo(
      init?.hasSchedulePreferences,
      false,
    );
    this.hasInheritedSchedulePreferences = defaultTo(
      init?.hasInheritedSchedulePreferences,
      false,
    );
    this.defaultScheduleTemplateId = defaultTo(
      init?.defaultScheduleTemplateId,
      undefined,
    );
    this.entityTitle = defaultTo(init?.entityTitle, '');
    this.isActive = defaultTo(init?.isActive, false);
  }

  get uniqueId() {
    return `${this.entityId.toString()}_${this.entityType.toString()}`;
  }

  getStringWhenEntityType(strings?: {
    vet?: string | undefined;
    template?: string | undefined;
  }): string | undefined {
    switch (this.entityType) {
      case EntityScheduleType.Veterinarian:
        return strings?.vet ?? undefined;
      case EntityScheduleType.Template:
        return strings?.template ?? undefined;
      case EntityScheduleType.Technician:
        return 'Technician';
      case EntityScheduleType.Unassigned:
        return 'Unassigned';
      default:
        break;
    }
    return undefined;
  }

  /**
   * Converts the HospitalScheduleEntityModel to a HospitalScheduleRequestModel and returns it.  This handles, based on EntityType, whether vet or template id should be set on the request model.
   * @returns
   */
  toRequestModel() {
    return new HospitalScheduleRequestModel({
      hospitalScheduleId: this.hospitalScheduleId,
      veterinarianId:
        this.entityType === EntityScheduleType.Veterinarian
          ? this.entityId
          : undefined,
      hospitalScheduleTemplateId:
        this.entityType === EntityScheduleType.Template
          ? this.entityId
          : undefined,
      hospitalId: this.hospitalId,
      date: this.day.toISO(),
      rowVersion: this.rowVersion,
    });
  }

  /**
   * Create a new HospitalScheduleEntityModel from a vet or a template and set the appropriate entity type, id, and schedulingColor fields for the entity.  Also determines the hasSchedulePreferences and hasInheritedSchedulePreferences
   * values based on the ProcessedHospitalSchedule supplied
   * @param entity
   * @param overrides
   * @returns
   */
  static createNewScheduleEntityFromEntity<
    T extends
      | schedule.HospitalScheduleVeterinarianModel
      | schedule.ScheduleHospitalScheduleTemplateListingModel,
  >(
    hospitalId: number,
    processedSchedule: ProcessedHospitalScheduleModel,
    entity: T,
    overrides?: Partial<HospitalScheduleDayEntityModel>,
    checkDate?: DateTime | undefined,
  ) {
    const base = new HospitalScheduleDayEntityModel({
      hospitalScheduleId: 0,
      rowVersion: '',
      isAdhoc: false,
    });

    var defaultTemplate = first(
      processedSchedule.getTemplates(hospitalId, { isDefault: true }),
    );

    //entity is vet
    if ('veterinarianId' in entity) {
      const vet = entity as schedule.HospitalScheduleVeterinarianModel;
      base.entityType = EntityScheduleType.Veterinarian;
      base.entityId = vet.veterinarianId;
      base.entityName = vet.fullName;
      base.entityTitle = vet.titleFullName;
      base.schedulingColor = vet.schedulingColor;
      base.defaultScheduleTemplateId =
        defaultTemplate?.hospitalScheduleTemplateId;
      base.isActive = vet.isActive();
    }

    //entity is template
    if ('hospitalScheduleTemplateId' in entity) {
      const template =
        entity as schedule.ScheduleHospitalScheduleTemplateListingModel;
      base.entityType = EntityScheduleType.Template;
      base.entityId = template.hospitalScheduleTemplateId;
      base.entityName = template.name;
      base.entityTitle = template.name;
      base.schedulingColor = template.color;
      base.isActive = template.isActive;
    }

    const ret = new HospitalScheduleDayEntityModel({
      ...base,
      ...overrides,
    });

    HospitalScheduleDayEntityModel.updateEntityPreferenceFields(
      processedSchedule,
      ret,
      checkDate,
    );
    return ret;
  }

  static updateEntityPreferenceFields(
    processedSchedule: ProcessedHospitalScheduleModel,
    entity: HospitalScheduleDayEntityModel,
    checkDate: DateTime | undefined,
  ) {
    const day =
      checkDate !== undefined
        ? WeekDay.fromDateTime(checkDate)
        : WeekDay.fromDateTime(entity.day);
    const preferenceCount = processedSchedule.entitySchedulePreferences.filter(
      (p) =>
        p.entityType === entity.entityType &&
        p.entityId === entity.entityId &&
        (checkDate === undefined || day === p.dayOfWeek),
    ).length;
    let inheritedCount = 0;

    if (preferenceCount <= 0) {
      const defaultTemplate = first(
        processedSchedule.getTemplates(entity.hospitalId, {
          day: entity.day,
          isDefault: true,
        }),
      );
      if (defaultTemplate) {
        inheritedCount = processedSchedule.entitySchedulePreferences.filter(
          (p) =>
            p.entityType === EntityScheduleType.Template &&
            p.entityId === defaultTemplate.hospitalScheduleTemplateId &&
            (checkDate === undefined || day === p.dayOfWeek),
        ).length;
      }
    }

    entity.hasSchedulePreferences = preferenceCount > 0;
    entity.hasInheritedSchedulePreferences = inheritedCount > 0;
  }
}
